import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, Sparkles, Heart } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Chat() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [otherUser, setOtherUser] = useState(null);
  const [starters, setStarters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const currentUserId = JSON.parse(localStorage.getItem('user') || '{}').id;

  useEffect(() => {
    loadChatData();
    const interval = setInterval(loadMessages, 3000);
    return () => clearInterval(interval);
  }, [matchId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      
      // Load messages
      await loadMessages();

      // Load conversation starters
      const startersResponse = await axios.get(`${API}/chat/${matchId}/starters`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStarters(startersResponse.data.suggestions);

      // Get match info to find other user
      const matchesResponse = await axios.get(`${API}/matches`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const currentMatch = matchesResponse.data.find(m => m.match.id === matchId);
      if (currentMatch) {
        setOtherUser(currentMatch.user);
      }
    } catch (error) {
      toast.error('Erreur de chargement du chat');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/chat/${matchId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
    } catch (error) {
      // Silent fail for polling
    }
  };

  const handleSendMessage = async (content = newMessage) => {
    if (!content.trim() || sending) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('content', content);

      await axios.post(`${API}/chat/${matchId}/messages`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setNewMessage('');
      loadMessages();
    } catch (error) {
      toast.error('Erreur d\'envoi du message');
    } finally {
      setSending(false);
    }
  };

  const handleStarterClick = (starter) => {
    handleSendMessage(starter);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-pink-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <div className="glass-card p-4 mb-4">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <button
            data-testid="back-btn"
            onClick={() => navigate('/matches')}
            className="p-2 hover:bg-white/50 rounded-full"
            style={{ transition: 'background 0.2s' }}
          >
            <ArrowLeft className="w-6 h-6 text-gray-700" />
          </button>

          {otherUser && (
            <>
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-pink-400 to-orange-400 flex items-center justify-center">
                {otherUser.photos && otherUser.photos.length > 0 ? (
                  <img
                    src={`${API}/profile/photos/${otherUser.photos[0]}`}
                    alt={otherUser.name}
                    className="w-full h-full rounded-full object-cover"
                  />
                ) : (
                  <Heart className="w-6 h-6 text-white" />
                )}
              </div>
              <div className="flex-1">
                <h2 className="font-bold text-gray-800">{otherUser.name}</h2>
                <p className="text-sm text-gray-600">{otherUser.location}</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 pb-6">
        <div className="max-w-4xl mx-auto">
          {messages.length === 0 ? (
            <div className="text-center py-12">
              <Sparkles className="w-16 h-16 mx-auto mb-4 text-pink-500" />
              <h3 className="text-xl font-bold text-gray-800 mb-4">Démarrez la conversation !</h3>
              <p className="text-gray-600 mb-6">Voici quelques suggestions pour briser la glace :</p>
              
              <div className="space-y-3 max-w-md mx-auto">
                {starters.map((starter, index) => (
                  <button
                    key={index}
                    data-testid={`starter-${index}`}
                    onClick={() => handleStarterClick(starter)}
                    className="w-full glass-card p-4 text-left hover:scale-105"
                    style={{ transition: 'transform 0.2s' }}
                  >
                    <p className="text-gray-700">{starter}</p>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4 py-4">
              {messages.map((message) => {
                const isOwn = message.sender_id === currentUserId;
                return (
                  <div
                    key={message.id}
                    data-testid={`message-${message.id}`}
                    className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[70%] px-4 py-3 rounded-2xl ${
                        isOwn
                          ? 'bg-gradient-to-r from-pink-500 to-orange-400 text-white'
                          : 'glass-card text-gray-800'
                      }`}
                    >
                      <p>{message.content}</p>
                      <p className={`text-xs mt-1 ${isOwn ? 'text-white/70' : 'text-gray-500'}`}>
                        {new Date(message.created_at).toLocaleTimeString('fr-FR', {
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </p>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>
      </div>

      {/* Message Input */}
      <div className="glass-card p-4">
        <div className="max-w-4xl mx-auto flex gap-3">
          <input
            data-testid="message-input"
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder="Écrivez votre message..."
            className="flex-1 px-4 py-3 rounded-full border border-gray-300 focus:ring-2 focus:ring-pink-500 focus:border-transparent"
          />
          <button
            data-testid="send-btn"
            onClick={() => handleSendMessage()}
            disabled={!newMessage.trim() || sending}
            className="p-3 bg-gradient-to-r from-pink-500 to-orange-400 text-white rounded-full hover:shadow-lg disabled:opacity-50"
            style={{ transition: 'box-shadow 0.2s' }}
          >
            <Send className="w-6 h-6" />
          </button>
        </div>
      </div>
    </div>
  );
}
